import { useNavigate } from '@tanstack/react-router';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import { Toggle } from 'components/Toggle/Toggle';
import useCountry from 'hooks/useCountry';
import { useGetVerificationStatus } from 'hooks/useGetVerificationStatus';
import { useOtpEnabled } from 'hooks/useOtpStatus';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import { useGetProfileQuery } from 'store/slices/profile-api';
import appHelpers from 'utils/app-urls';
import verification from '../../../../../data/verification';
import verificationStatus from '../../../../../data/verificationStatus';
import messages from './messages';

const cdnURL = appHelpers.getCDNUrl();

export default function ProfileView({
  showModal,
  toggleLoginNotifications,
  closeModal,
}: {
  showModal?: boolean;
  toggleLoginNotifications?: (
    newValue: boolean,
    modalConfirmed: boolean,
  ) => void;
  closeModal?: () => void;
}) {
  const { data: userProfileResponse } = useGetProfileQuery();
  const { language } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();
  const status = useGetVerificationStatus();
  const verificationState = useRef('');
  const countries = useCountry();
  const otpEnabled = useOtpEnabled();

  const { profileViewMessages } = messages[language];

  function getCountryName(countryCode: string | undefined): string {
    if (!countryCode) return '';
    const country = countries?.find(x => x.code === countryCode);

    if (country == null) return '';

    return country.name;
  }
  function updateButtons(verificationStatusToCheck: string | undefined) {
    if (!verificationStatusToCheck) return;
    const { actions } = status;
    verificationState.current = verification.Unverified;

    if (
      verificationStatusToCheck?.toUpperCase() ===
      verificationStatus.Verified.toUpperCase()
    ) {
      verificationState.current = verification.Verified;
    } else {
      if (
        verificationStatusToCheck?.toUpperCase() ===
        verificationStatus.Pending.toUpperCase()
      )
        verificationState.current = verification.Pending;

      if (
        actions?.find(
          x =>
            x.actionCode === 'ID' &&
            x.statusCode === verificationStatus.PendingUser,
        )
      )
        verificationState.current = verification.SMS;
    }
  }

  useEffect(() => {
    updateButtons(userProfileResponse?.verificationStatus ?? undefined);
  }, [userProfileResponse?.verificationStatus]);

  updateButtons(userProfileResponse?.verificationStatus ?? undefined);
  const twoFactorEnabled = userProfileResponse?.twoFactorStatus?.activated;
  const emailNotificationEnabled = userProfileResponse?.loginNotification;

  function getModalContent() {
    return (
      <div>
        <div className="mb-8">
          <img
            src={`${cdnURL}assets/components/modal/exclamation.svg`}
            alt={profileViewMessages.disableNotificationsAlt}
            className="center"
          />
        </div>
        <p>{profileViewMessages.disableNotificationsModalBody}</p>
        <div className="mt-4">
          <Button
            onClick={() => {
              if (toggleLoginNotifications)
                toggleLoginNotifications(false, true);
            }}
            fluid
            small
            className="md:w-36 w-24  mr-2">
            {profileViewMessages.yesButton}
          </Button>
          <Button
            onClick={() => {
              if (closeModal) closeModal();
            }}
            fluid
            small
            className="md:w-36 w-24 ml-2"
            secondary>
            {profileViewMessages.noButton}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <h1 className="font-semibold text-2.5x1 text-center mb-2">
          {userProfileResponse?.firstName}&nbsp;
          {userProfileResponse?.lastName}&nbsp;
          {verificationState.current === verification.Verified ? (
            <img
              className="inline-block mb-1"
              src={`${cdnURL}assets/components/icon/tick.svg`}
              alt={profileViewMessages.tickAlt}
            />
          ) : null}
        </h1>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="h-8 w-8 flex items-center justify-center overflow-hidden">
                <img
                  src={`${cdnURL}assets/products/icons/${userProfileResponse?.displayCurrency?.code?.toLowerCase()}.svg`}
                  alt={userProfileResponse?.baseCurrency?.code?.toLowerCase()}
                  className="w-full rounded-full"
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.countryLabel}
              </div>
              <div className="text-header-text -mt-1">
                {getCountryName(userProfileResponse?.countryCode ?? undefined)}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block mb-1"
                  src={`${cdnURL}assets/components/icon/verifiy.svg`}
                  alt={profileViewMessages.verifyAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.verificationStatusLabel}
              </div>
              <div className="text-header-text -mt-1">
                {userProfileResponse?.verificationStatus}
              </div>
            </div>
          </div>
          <div className="flex flex-col h-12 justify-center ml-auto">
            {verificationState.current === verification.Unverified ? (
              <Button
                secondary
                type="button"
                self-end
                className="xs:h-auto xs:px-3 p-2 w-auto"
                onClick={() => navigate({ to: '/verification' })}>
                {profileViewMessages.verifyButton}
              </Button>
            ) : null}
            {/* {verificationState.current === verification.Pending ? (
              <Button
                secondary
                type="button"
                self-end
                className="xs:h-auto xs:px-3 p-2 w-auto"
                onClick={() =>
                  navigate({ to: '/verification/link-sent-to-mobile' })
                }>
                {profileViewMessages.verificationPendingButton}
              </Button>
            ) : null}
            {verificationState.current === verification.Source ? (
              <Button
                secondary
                type="button"
                self-end
                className="xs:h-auto xs:px-3 p-2 w-auto"
                onClick={() =>
                  navigate({ to: '/verification/documents-source' })
                }>
                {profileViewMessages.sourceOfFundsButton}
              </Button>
            ) : null}
            {verificationState.current === verification.SMS ? (
              <Button
                secondary
                type="button"
                self-end
                className="xs:h-auto xs:px-3 p-2 w-auto"
                onClick={() => navigate({ to: '/verification/phone-number' })}>
                {profileViewMessages.sendSmsButton}
              </Button>
            ) : null} */}
          </div>
        </div>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block w-6 lg:w-auto"
                  src={`${cdnURL}assets/components/icon/otp.svg`}
                  alt={profileViewMessages.otpAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.otpStatusLabel}
              </div>
              <div className="text-header-text -mt-1">
                {otpEnabled
                  ? profileViewMessages.otpEnabled
                  : profileViewMessages.otpDisabled}
              </div>
            </div>
          </div>
          {!otpEnabled && (
            <div className="flex flex-col h-12 justify-center ml-auto">
              <Button
                secondary
                type="button"
                self-end
                onClick={() => navigate({ to: '/settings/otp/enable' })}
                className="xs:h-auto xs:px-3 p-2 w-auto">
                {profileViewMessages.enableOtpButton}
              </Button>
            </div>
          )}
        </div>

        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block mb-1"
                  src={`${cdnURL}assets/components/icon/two-factor.svg`}
                  alt={profileViewMessages.twoFactorAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.twoFactorStatusLabel}
              </div>
              <div className="text-header-text -mt-1">
                {twoFactorEnabled
                  ? profileViewMessages.twoFactorEnabled
                  : profileViewMessages.twoFactorDisabled}
              </div>
            </div>
          </div>
          <div className="flex flex-col h-12 justify-center ml-auto">
            <Button
              secondary
              type="button"
              self-end
              onClick={() => navigate({ to: '/settings/two-factor' })}
              className="xs:h-auto xs:px-3 p-2 w-auto">
              {twoFactorEnabled
                ? profileViewMessages.disabledTwoFactorButton
                : profileViewMessages.enableTwoFactorButton}
            </Button>
          </div>
        </div>
        <div className="bg-background px-4 py-3 text-header-text text-left rounded-lg mb-2 flex">
          <div className="flex h-12 items-center">
            <div className="w-12 flex items-center">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  className="inline-block mb-1"
                  src={`${cdnURL}assets/components/icon/email.svg`}
                  alt={profileViewMessages.emailNotificationAlt}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-body-text text-sm">
                {profileViewMessages.emailNotificationLabel}
              </div>
              <div className="text-header-text -mt-1">
                {emailNotificationEnabled
                  ? profileViewMessages.emailNotificationEnabled
                  : profileViewMessages.emailNotificationDisabled}
              </div>
            </div>
          </div>
          <div className="flex flex-col h-12 justify-center ml-auto">
            <Toggle
              self-end
              value={emailNotificationEnabled ?? false}
              onChange={(newValue: boolean) => {
                if (toggleLoginNotifications)
                  toggleLoginNotifications(newValue, false);
              }}
            />
          </div>
        </div>
      </div>
      <Modal
        hideModal={() => {
          if (closeModal) closeModal();
        }}
        show={showModal || false}
        content={getModalContent()}
        closeModal={() => {
          if (closeModal) closeModal();
        }}
      />
    </>
  );
}
